import { CrossVisionObj, Measure, TranslatedEmb } from '@wizeoo/akm-message-lib';
import { BaselineEngine, INJECTION } from '../../manager/workingdata/baseline.engine';
import { WizConstants } from '../../model/constants';
import { IVo, entity, field } from '../entity-maps';
import { WizMathUtils } from '@wizeoo/akm-core-lib';

/**
 * VO représentant une mesure permettant de calculer la valeur d'une ligne
 */

@entity('wizeoo.allkeyme.workingdata.message.Measure', 'VISION')
export class MeasureVO implements IVo {

    /** L'objet source provenant du back */
    private _source: Measure;

    constructor(pSource?: Measure) {
        if (pSource) {
            this._source = pSource;
        } else {
            this._source = new Measure();
            this._source.uid = WizMathUtils.createUUID(); // génération du UID si nouveau
        }
    }

    public get source(): Measure {
        return this._source;
    }
    public set source(value: Measure) {
        this._source = value;
    }

    // Id technique
    public get id(): number {
        return this.source.id;
    }
    public set id(value: number) {
        this.source.id = value;
    }

    // UID technique
    public get uid(): string {
        return this.source.uid;
    }
    public set uid(value: string) {
        this.source.uid = value;
    }

    public get version(): number {
        // technique
        return this.source.version;
    }
    public set version(value: number) {
        this.source.version = value;
    }
    /**
     * Le code de la measure
     */
    @field('string')
    public set code(value: string) {
        this.source.code = value;
    }
    public get code(): string {
        return this.source.code;
    }

    /** Nom court */
    @field('string')
    public set shortCode(shortCode: string) {
        this.source.shortCode = shortCode;
    }
    public get shortCode(): string {
        return this.source.shortCode;
    }

    // Id technique de la vision
    public get vid(): number {
        return this.source.vid;
    }
    public set vid(value: number) {
        this.source.vid = value;
    }

    // code domaine (root), technique
    public get domain(): string {
        return this._source.domain;
    }
    public set domain(value: string) {
        this._source.domain = value;
    }

    public get businessUnitCode(): string {
        // code de la BU
        return this._source.businessUnitCode;
    }
    public set businessUnitCode(value: string) {
        this._source.businessUnitCode = value;
    }

    /* Le code vision */
    @field('string')
    public set vision(value: string) {
        this._source.vision = value;
    }
    public get vision(): string {
        return this._source.vision;
    }

    /**
     * Le code step (* pour vision)
     */
    @field('number')
    public set stepLevel(value: number) {
        this._source.stepLevel = value;
    }
    public get stepLevel(): number {
        return this._source.stepLevel;
    }

    /**
     * Le mode à appliquer sur la mesure;
     */
    @field('string')
    public set mode(value: string) {
        this._source.mode = value;
    }
    public get mode(): string {
        return this._source.mode;
    }

    /**
     * champ I éditable
     */
    @field('boolean')
    public set editable(value: boolean) {
        this._source.editable = value;
    }
    public get editable(): boolean {
        return this._source.editable;
    }

    /**
     * Formule héritée FROM
     */
    @field('string')
    public set formulaF(value: string) {
        this._source.formulaF = value;
    }
    public get formulaF(): string {
        return this._source.formulaF;
    }

    /**
     * Formule interaction
     */
    @field('string')
    public set formulaI(value: string) {
        this._source.formulaI = value;
    }
    public get formulaI(): string {
        return this._source.formulaI;
    }

    /**
     * Formule héritée SUBMIT
     */
    @field('string')
    public set formulaS(value: string) {
        this._source.formulaS = value;
    }
    public get formulaS(): string {
        return this._source.formulaS;
    }

    /**
     * Formule TO, aggrégat du step inférieur
     */
    @field('string')
    public set formulaT(value: string) {
        this._source.formulaT = value;
    }
    public get formulaT(): string {
        return this._source.formulaT;
    }

    /**
     * Le type de donnée
     */
    @field('string')
    public set datakind(value: string) {
        this._source.dataKind = value;
    }
    public get datakind(): string {
        return this._source.dataKind;
    }

    @field('TranslatedEmb')
    public set label(value: TranslatedEmb) {
      this.source.label = value;
    }
    public get label(): TranslatedEmb {
      return this.source.label;
    }
  
    @field('TranslatedEmb')
    public set description(value: TranslatedEmb) {
      this.source.description = value;
    }
    public get description(): TranslatedEmb {
      return this.source.description;
    }
  
    @field('TranslatedEmb')
    public set valueControlMessage(value: TranslatedEmb) {
      this.source.valueControlMessage = value;
    }
    public get valueControlMessage(): TranslatedEmb {
      return this.source.valueControlMessage;
    }
  
    @field('TranslatedEmb')
    public set editRestrictMessage(value: TranslatedEmb) {
      this.source.editRestrictMessage = value;
    }
    public get editRestrictMessage(): TranslatedEmb {
      return this.source.editRestrictMessage;
    }  
    
    /**
     * négatif autorisé
     */
    @field('boolean')
    public set allowNegative(value: boolean) {
        this.source.allowNegative = value;
    }
    public get allowNegative(): boolean {
        return this.source.allowNegative;
    }

    /**
     * valeur max, peut être une formule
     */
    @field('string')
    public set maxValue(value: string) {
        this.source.maxValue = value;
    }
    public get maxValue(): string {
        return this.source.maxValue;
    }

    /**
     * valeur min, peut être une formule
     */
    @field('string')
    public set minValue(value: string) {
        this.source.minValue = value;
    }
    public get minValue(): string {
        return this.source.minValue;
    }

    /**
     * nombre de décimales (pour le calcul)
     */
    @field('number')
    public set nbDecimal(value: number) {
        this.source.nbDecimal = value;
    }
    public get nbDecimal(): number {
        return this.source.nbDecimal;
    }

    /**
     * expression pour contrôle de cohérence
     */
    @field('string')
    public set valueControl(value: string) {
        this.source.valueControl = value;
    }
    public get valueControl(): string {
        return this.source.valueControl;
    }

    /**
     * expression pour autorisation de saisie
     */
    @field('string')
    public set editRestrict(value: string) {
        this.source.editRestrict = value;
    }
    public get editRestrict(): string {
        return this.source.editRestrict;
    }

    /**
     * expression pour alimentation de la valeur par défaut
     */
    @field('string')
    public set defaultValue(value: string) {
        this.source.defaultValue = value;
    }
    public get defaultValue(): string {
        return this.source.defaultValue;
    }

    /**
     * expression pour déclenchement par trigger
     */
    @field('string')
    public set triggerValue(value: string) {
        this.source.triggerValue = value;
    }
    public get triggerValue(): string {
        return this.source.triggerValue;
    }


    /**
     * code du format de donnée (bu|code)
     */
    @field('string')
    public set formatCode(value: string) {
        this.source.formatCode = value;
    }
    public get formatCode(): string {
        return this.source.formatCode;
    }

    /**
     * code du style (vision|code)
     */
    @field('string')
    public set styleCode(value: string) {
        this.source.styleCode = value;
    }
    public get styleCode(): string {
        return this.source.styleCode;
    }

    /**
     * restriction sur texte
     */
    @field('string')
    public set editRegex(value: string) {
        this.source.editRegex = value;
    }
    public get editRegex(): string {
        return this.source.editRegex;
    }

    /**
     * configuration complexe (mesures de type liste)
     */
    @field('string')
    public set settings(settings: any) {
        this._source.settings = settings;
    }
    public get settings(): any {
        return this._source.settings;
    }

    /**
     * gestion des settings en mode string pour simplification
     */
    private _settingsStr: string = null;
    public set settingsStr(value: string) {
        this._settingsStr = value;
    }

    public get settingsStr(): string {
        return this._settingsStr;
    }

    @field('boolean')
    public set upper(value: boolean) {
      this.source.upper = value;
    }
    public get upper(): boolean {
      return this.source.upper;
    }

    /**
     * aggrégat par défaut
     */
    @field('string')
    public set defaultAgg(value: string) {
        this.source.defaultAgg = value;
    }
    public get defaultAgg(): string {
        return this.source.defaultAgg;
    }


    /**
     * lien pour synchro cross-visions
     */
    @field('string')
    public set linkedRef(value: CrossVisionObj) {
        this.source.linkedRef = value;
    }
    public get linkedRef(): CrossVisionObj {
        return this.source.linkedRef as CrossVisionObj;
    }

    /**
     * gestion des settings en mode string pour simplification
     */
    private _linkedRefStr: string = null;
    public set linkedRefStr(value: string) {
        this._linkedRefStr = value;
    }

    public get linkedRefStr(): string {
        return this._linkedRefStr;
    }


  /* ################################################
      accesseurs volatiles pour manipulations rapides
  ################################################ */

  /** true si constante */
  public get isConstant(): boolean {
    return this.mode === WizConstants.MEASURE_MODE_CONSTANT;
  }

  /** les weightGrids sont ajoutés en fake dans la grille des measures */
  public get isWeight(): boolean {
    return this.mode === WizConstants.MEASURE_MODE_WEIGHT_GRID;
  }


  /* ################################################
      données de rendu (IHM)
  ################################################ */
  // public measureProperties: Map<string, any> = new Map(); // les propriétés complémentaires sur la mesure pour le rendu IHM

  // l'un des modes de cette mesure est en erreur (alimenté depuis le errorMMs généré par le buildMatrix)
  public hasError: boolean = false; 

  /**
   * retourne true si la mesure est actuellement sélectionnée dans le panel de sélection de la vue mesure
   */
  public get selected(): boolean {
    return BaselineEngine.injectorCall(INJECTION.MEASURE_SELECTED, this.source) || false;
  }

  /**
   * retourne true si la mesure est actuellement sélectionnée dans le panel de sélection de la vue mesure
   */
  public get noDependency(): boolean {
    return BaselineEngine.injectorCall(INJECTION.MEASURE_NO_DEPENDENCY, this.source) || false;
  }

  /**
   * libellé
   */
  public get trsLabel(): string {
    return BaselineEngine.injectorCall(INJECTION.MEASURE_LABEL, this.source) || null;
  }

  /**
   * retour le label (si existe ) + le code entre ()
   */
  public get trsFullLabel(): string {
    return this.trsLabel + ' (' + this.shortCode + ')';
  }

  /**
   * description
   */
  public get trsDescription(): string {
    return BaselineEngine.injectorCall(INJECTION.MEASURE_DESC, this.source) || null;
  }

  /**
   * Return msgError sur contrôle de cohérence
   * @return string
   */
  public  get measureControlMessage(): string {
    return BaselineEngine.injectorCall(INJECTION.MEASURE_CONTROL_MESSAGE, this.source) || null;
  }

  /**
   * Return msgError sur restriction en édition
   * @return string
   */
  public get measureEditRestrictMessage(): string {
    return BaselineEngine.injectorCall(INJECTION.MEASURE_EDIT_RESTRICT_MESSAGE, this.source) || null;
  }


  

}
